import EditorActions from 'components/Common/EditorActions'
import CourtSurfaceChoices, { surfaceFields, surfacesOf } from "./CourtSurfaceChoices"
import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { Prompt } from "react-router-dom"
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import { useFormik } from "formik"
import toastr from "toastr"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import LocalizedTextBlock from "components/Common/LocalizedTextBlock"
import DateTimeField, { dateFieldMode } from "components/Common/DateTimeField"
import CreateEventChoices from "./CreateEventChoices"
import CreateEventLogistics from "./CreateEventLogistics"
import {
  BALLS_TBD,
  officialBallFrom,
  officialBallOptions,
  initialEventValues,
  validateEventCreation,
  buildEventCreationPayload,
  creationDescription,
  creationPackage,
  descriptionWritten,
  packageWritten,
  provisionalVenue,
  eventCountry,
  eventTimezone,
  eventTimezones,
  eventZoneLabel,
  endFromStart,
  sportChoices,
} from "./eventCreationForm"
import { eventDefaultImagesKey, TENIS10, tenis10FallbackImageUrl } from "./tenis10Event"
import { runEventCreation } from "./eventCreationOperation"
import { PLAYER_PACKAGE_FIELDS } from "./eventPlayerPackage"
import { DESCRIPTION_FIELDS } from "./eventTexts"
import { eventDirectoryLocation } from "./directoryQuery"
import { invalidateEventCache } from "./eventCachePolicy"
import "./eventCreation.scss"

const readPending = (key) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key))
    return saved && /^[a-zA-Z0-9-]{16,128}$/.test(saved.operationId)
      ? saved
      : null
  } catch (_error) {
    return null
  }
}
const writePending = (key, value) => {
  try {
    value
      ? sessionStorage.setItem(key, JSON.stringify(value))
      : sessionStorage.removeItem(key)
  } catch (_error) {
    /* The in-memory operation remains safe if browser storage is unavailable. */
  }
}
const newOperationId = () => {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  )
}
const titleCase = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : ""

export function CreateEvent({ settings = {}, userDetails = {}, history }) {
  const socketContext = useContext(socketStore)
  const { socket, socketClientId } = socketContext?.state?.socket || {}
  const storageKey =
    "sportya-event-creation:" + (userDetails.id || userDetails._id)
  const initialValues = useMemo(
    () => readPending(storageKey)?.values || initialEventValues(settings),
    [settings, storageKey]
  )
  const pending = useRef(readPending(storageKey))
  const operation = useRef(null)
  const busy = useRef(false)
  const leaving = useRef(false)
  const formElement = useRef(null)
  const [status, setStatus] = useState(pending.current ? "unknown" : "idle")
  const [message, setMessage] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [organizersReady, setOrganizersReady] = useState(true)
  // The end the start filled in, until the author changes it (P24).
  const [filledEnd, setFilledEnd] = useState(null)
  // The Logistics card must have loaded, so that the suppliers are decided knowingly.
  const [logisticsStatus, setLogisticsStatus] = useState("idle")
  const [exitLocation, setExitLocation] = useState(null)
  const locked = status === "pending" || status === "unknown"

  const trackCreation = async (request, retry = true) => {
    if (busy.current) return
    busy.current = true
    pending.current = request
    writePending(storageKey, request)
    setStatus("pending")
    setMessage("")
    operation.current = runEventCreation({
      operationId: request.operationId,
      socket,
      read: (id) => get("/admin/event-creations/" + id),
      submit:
        retry && request.payload
          ? (id) =>
              post("/admin/events", {
                ...request.payload,
                operationId: id,
                socketClientId,
              })
          : undefined,
    })
    const result = await operation.current.promise
    busy.current = false
    if (result.status === "cancelled") return
    setStatus(result.status)
    if (result.status === "saved") {
      pending.current = null
      writePending(storageKey, null)
      leaving.current = true
      invalidateEventCache({ eventId: result.id })
      if (result.warning)
        toastr.warning(
          "The event was created, but delivery of the organizer notification was not confirmed."
        )
      history.push("/events/" + result.id + "/details")
    } else if (result.status === "error") {
      pending.current = null
      writePending(storageKey, null)
      setMessage(result.message)
    }
  }
  const form = useFormik({
    initialValues,
    enableReinitialize: true,
    validate: (values) => {
      const errors = validateEventCreation(values, settings)
      if (!errors.category && !errors.officialBall && logisticsStatus !== "ready")
        errors.logistics = logisticsStatus === "error"
          ? "The Logistics products didn't load. Click Retry under Logistics."
          : "The Logistics products are still loading."
      return errors
    },
    onSubmit: async (values) => {
      if (busy.current || locked || !organizersReady) return
      await trackCreation({
        operationId: newOperationId(),
        payload: buildEventCreationPayload(values, settings),
        values,
      })
    },
  })
  const { values, errors, touched, validateForm } = form
  // A Tenis10 event (PRD 16 LR11, the LR10 verdict E-C): the Sport select's third choice.
  const tenis10 = values.sportType === TENIS10
  // The Logistics card's status takes part in the validation, so the error summary follows it.
  useEffect(() => {
    if (submitted) validateForm()
  }, [logisticsStatus, submitted, validateForm])
  useEffect(() => () => operation.current?.cancel(), [])
  const unsaved = (form.dirty || locked) && !leaving.current
  useEffect(() => {
    const beforeUnload = (event) => {
      if (!leaving.current) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    if (unsaved) window.addEventListener("beforeunload", beforeUnload)
    return () => window.removeEventListener("beforeunload", beforeUnload)
  }, [unsaved])

  const submit = async (event) => {
    event.preventDefault()
    if (locked || busy.current) return
    setSubmitted(true)
    const invalid = await form.validateForm()
    await form.setTouched(
      Object.fromEntries(Object.keys(values).map((key) => [key, true])),
      false
    )
    if (Object.keys(invalid).length) {
      const first = Object.keys(invalid)
        .map((key) => document.getElementById("create-" + key))
        .filter(Boolean)
        .sort((a, b) => (a.compareDocumentPosition(b) & 2 ? 1 : -1))[0]
      first?.focus()
      return
    }
    await form.submitForm()
  }
  const focusField = (key) => document.getElementById("create-" + key)?.focus()
  const field = (
    name,
    label,
    { type = "text", options, required = false, hint, ...props } = {}
  ) => {
    const invalid = Boolean(touched[name] && errors[name])
    const shared = {
      id: "create-" + name,
      name,
      value: values[name] ?? "",
      onBlur: form.handleBlur,
      onChange: form.handleChange,
      className: "form-control",
      "aria-invalid": invalid,
      "aria-required": required,
      "aria-describedby": "create-" + name + "-help",
      ...props,
    }
    return (
      <div key={name} className={props.className || ""}>
        <label htmlFor={shared.id}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </label>
        {options ? (
          <select {...shared}>
            <option value="">Select…</option>
            {options.map((option) => (
              <option key={option.key} value={option.key}>
                {option.name}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea {...shared} rows={3} />
        ) : dateFieldMode(type) ? (
          <DateTimeField {...shared} mode={dateFieldMode(type)} />
        ) : (
          <input {...shared} type={type} />
        )}
        <small
          id={shared.id + "-help"}
          className={invalid ? "text-danger" : "text-muted"}
        >
          {invalid ? errors[name] : hint}
        </small>
      </div>
    )
  }
  const coordinatorOptions = (settings.coordinators || []).map((option) => ({
    key: option.name,
    name: option.name,
  }))
  // A Tenis10 event takes the club's Tenis10 images of the category, an adult one the category's (Marian 2026-10-29).
  const defaultImagesKey = eventDefaultImagesKey({ programType: tenis10 ? TENIS10 : undefined, category: values.category })
  const defaultImages = values.club?.defaultEventImages?.[defaultImagesKey] || {}
  // The images the event will show: the club's of that set; where the club has none, a Tenis10 event shows the Sportya Tenis10 image of its
  // category, so the preview shows and names it (Tudor 2026-10-29, variant A). An adult event previews the club's images only, as before.
  const previews = ["logo", "featured", "cover"].flatMap((kind) => {
    if (defaultImages[kind]) {
      const src = `${process.env.REACT_APP_API_URL}/assets/clubs/${values.club.clubId}/${defaultImagesKey}/${defaultImages[kind]}`
      return [{ kind, src, caption: titleCase(kind) }]
    }
    return tenis10 && values.category
      ? [{ kind, src: tenis10FallbackImageUrl(values.category, kind), caption: titleCase(kind) + " · Sportya Tenis10", sportya: true }]
      : []
  })
  const sportyaPreviews = previews.filter((preview) => preview.sportya).length
  const presentationNote = !tenis10
    ? "Club images for the selected category are used by default."
    : !sportyaPreviews
      ? "The club's Tenis10 images for the selected category are used by default."
      : sportyaPreviews < previews.length
        ? "The club's Tenis10 images for the selected category are used by default, and the Sportya Tenis10 image where the club has none."
        : "The club has no Tenis10 images for the selected category, so the event takes the Sportya Tenis10 images below."
  const ready = [
    "activeSports",
    "coordinators",
    "categories",
    "playModes",
    "season",
    "courtSurfaces",
  ].every((key) => settings[key]?.length)
  if (!ready)
    return (
      <div className="alert alert-warning" role="alert">
        Event settings are unavailable.{" "}
        <button
          type="button"
          className="btn btn-link"
          onClick={() => window.location.reload()}
        >
          Reload settings
        </button>
      </div>
    )

  return (
    <form
      ref={formElement}
      className="event-creation"
      onSubmit={submit}
      noValidate
      aria-label="Create event"
    >
      <Prompt
        when={unsaved}
        message={(location, action) => {
          if (leaving.current) return true
          setExitLocation({ location, action })
          return false
        }}
      />
      <Modal isOpen={Boolean(exitLocation)} toggle={() => setExitLocation(null)}>
        <ModalHeader toggle={() => setExitLocation(null)}>Leave event creation?</ModalHeader>
        <ModalBody>{locked ? "Creation may still be in progress. Its status can be checked when you return." : "Your changes to this new event have not been saved."}</ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-light" onClick={() => setExitLocation(null)}>Keep editing</button>
          <button type="button" className="btn btn-primary" onClick={() => {
            leaving.current = true
            const target = exitLocation
            setExitLocation(null)
            if (target.action === "REPLACE") history.replace(target.location)
            else history.push(target.location)
          }}>Leave page</button>
        </ModalFooter>
      </Modal>
      <p className="text-muted mb-3">
        Add the event details below. Fields marked * are required.
      </p>
      {submitted && Object.keys(errors).length > 0 && (
        <div className="alert alert-danger" role="alert">
          <strong>Check the following fields:</strong>
          <ul className="mb-0">
            {Object.entries(errors).map(([key, error]) => (
              <li key={key}>
                <button type="button" onClick={() => focusField(key)}>
                  {error}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {message && (
        <div className="alert alert-danger" role="alert">
          {message}
        </div>
      )}
      {status === "unknown" && (
        <div className="alert alert-warning" role="alert">
          <strong>Creation has not been confirmed yet.</strong>
          <p>
            The event may already be saved. Check its status or retry the same
            request safely.
          </p>
          <button
            type="button"
            className="btn btn-outline-primary mr-2"
            onClick={() => trackCreation(pending.current, false)}
          >
            Check creation status
          </button>
          {pending.current?.payload && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => trackCreation(pending.current)}
            >
              Retry same request
            </button>
          )}
        </div>
      )}
      <fieldset disabled={locked}>
        <section className="creation-section">
          <h5>Event details</h5>
          {field("name", "Event name", { required: true, maxLength: 200 })}
          <div className="creation-grid four">
            {field("sportType", "Sport", {
              required: true,
              options: sportChoices(settings),
              hint: tenis10 && "Tenis10: children under 16, entered by their parents; a passport required. Chosen at creation; it does not change.",
            })}
            {field("category", "Category", {
              required: true,
              options: settings.categories,
              hint: tenis10 && "Tenis10: the badge on the event page. Children receive no points.",
            })}
            {field("playMode", "Format", {
              required: true,
              options: settings.playModes,
            })}
            {field("season", "Season", {
              required: true,
              options: settings.season,
            })}
          </div>
          <label className="creation-check">
            <input
              type="checkbox"
              name="featured"
              checked={values.featured}
              onChange={form.handleChange}
            />{" "}
            Featured event
          </label>
        </section>
        <section className="creation-section">
          <h5>Schedule &amp; venue</h5>
          <CreateEventChoices
            form={form}
            locked={locked}
            onReadiness={setOrganizersReady}
          />
          {provisionalVenue(values.club) && (
            <div className="creation-grid">
              {field("country", "Country", {
                required: true,
                options: (settings.eligibleCountries || [])
                  .filter((country) => eventTimezones[country.name])
                  .map((country) => ({
                    key: country.name,
                    name: country.name,
                  })),
              })}
              {field("city", "City", {
                hint: "Optional. You can add the city later.",
              })}
            </div>
          )}
          {!provisionalVenue(values.club) && values.club && (
            <p className="creation-note">
              {[values.club.location?.city, eventCountry(values)]
                .filter(Boolean)
                .join(", ")}
              {errors.country && (
                <span className="text-danger"> — {errors.country}</span>
              )}
            </p>
          )}
          <p className="creation-note">
            Timezone:{" "}
            <strong>
              {eventTimezone(values) || "Choose a club or country"}
            </strong>
            . All dates and times use this timezone.
          </p>
          <div className="creation-grid">
            {/* The end follows a new start until the author changes it: the next day at 18:00 (P24, Tudor 2026-10-24). */}
            {field("startDate", "Start date and time", {
              type: "datetime-local",
              required: true,
              zoneLabel: eventZoneLabel(eventTimezone(values)),
              onChange: (event) => {
                form.handleChange(event)
                const start = event.target.value
                if (!start || (values.endDate && values.endDate > start && values.endDate !== filledEnd)) return
                const end = endFromStart(start)
                setFilledEnd(end)
                form.setFieldValue("endDate", end)
              },
            })}
            {field("endDate", "End date and time", {
              type: "datetime-local",
              required: true,
              zoneLabel: eventZoneLabel(eventTimezone(values)),
              hint: filledEnd && values.endDate === filledEnd ? "Filled from the start: the next day, 18:00. Change it if needed." : undefined,
            })}
          </div>
          <div className="creation-grid three">
            <div>
              <CourtSurfaceChoices id="create-courtSurfaces" options={settings.courtSurfaces} value={surfacesOf(values)}
                error={(touched.courtSurfaces || form.submitCount > 0) && errors.courtSurfaces ? errors.courtSurfaces : undefined}
                onChange={(list) => { form.setFieldTouched("courtSurfaces", true, false); form.setValues({ ...values, ...surfaceFields(list) }) }} />
            </div>
            {field("numberOfCourts", "Number of courts", {
              type: "number",
              min: 0,
              step: 1,
            })}
            {/* A Tenis10 event has no official ball: the balls of each competition's colour, recommended to the club (the canvas verdict E-A). */}
            {tenis10 ? <div key="officialBall"><label>Official ball</label>
              <div className="form-control-plaintext text-muted">Tenis10: the balls of each competition’s colour, recommended to the club (Logistics).</div></div>
            : field("officialBall", "Official ball", {
              required: true,
              hint: "Choose a ball or Balls TBD.",
              value:
                values.officialBall === null
                  ? BALLS_TBD
                  : values.officialBall?.name || "",
              onChange: (event) => {
                // Balls TBD is null; the empty choice leaves the ball unchosen.
                const ball = officialBallFrom(event.target.value, settings.balls)
                form.setFieldValue("officialBall", ball === undefined ? "" : ball)
              },
              options: officialBallOptions(settings.balls),
            })}
          </div>
        </section>
        <CreateEventLogistics
          sportType={tenis10 ? "tennis" : values.sportType}
          programType={tenis10 ? TENIS10 : undefined}
          category={values.category}
          officialBall={values.officialBall}
          suppliers={values.logisticsSuppliers || {}}
          onSuppliers={(suppliers) =>
            form.setFieldValue("logisticsSuppliers", suppliers)
          }
          products={values.logisticsProducts || {}}
          onProducts={(products) =>
            form.setFieldValue("logisticsProducts", products)
          }
          onStatus={setLogisticsStatus}
        />
        <section className="creation-section">
          <h5>People &amp; participation</h5>
          <div className="creation-grid three">
            {field("coordinator", "Coordinator", {
              required: true,
              options: coordinatorOptions,
              value: values.coordinator?.name || "",
              onChange: (event) =>
                form.setFieldValue(
                  "coordinator",
                  settings.coordinators.find(
                    (option) => option.name === event.target.value
                  ) || null
                ),
            })}
            {field("directorName", "Tournament director")}
            {field("email", "Contact email", { type: "email" })}
            {field("phone", "Contact phone", { type: "tel" })}
            {field("refereeName", "Referee")}
            {field("supervisorName", "Supervisor")}
          </div>
          <div className="creation-grid">
            {/* A Tenis10 event: singles only, the entries counted per child. */}
            {/* One competition per child on Tenis10, fixed (round 6, round 7 „A”): the rule alone, without a field (P26, Tudor 2026-10-24);
              the values keep 1, which the creation sends. */}
            {tenis10 ? <div key="singlesMaxNumber"><label>Competitions per child</label>
              <div className="form-control-plaintext text-muted">Tenis10: one competition per child is allowed.</div></div>
            : field("singlesMaxNumber", "Singles entries per player", {
              required: true,
              options: [1, 2, 3].map((key) => ({ key, name: key })),
            })}
            {!tenis10 && field("doublesMaxNumber", "Doubles entries per player", {
              required: true,
              options: [1, 2, 3].map((key) => ({ key, name: key })),
            })}
          </div>
        </section>
        <LocalizedTextBlock
          id="create-playerPackage"
          className="creation-section"
          open={false}
          title={<>Player package <span>Optional</span></>}
          fields={PLAYER_PACKAGE_FIELDS}
          original={creationPackage(values, settings)}
          onLanguage={(language) => form.setFieldValue("playerPackageLanguage", language)}
          onContent={(content) => form.setValues({ ...values, ...packageWritten(values, settings, content) })}
        >
          <label className="creation-check">
            <input
              type="checkbox"
              name="tShirt"
              checked={values.tShirt}
              onChange={form.handleChange}
            />{" "}
            T-shirt included
          </label>
        </LocalizedTextBlock>
        <LocalizedTextBlock
          id="create-description"
          className="creation-section"
          open={false}
          title={<>Event description <span>Optional</span></>}
          fields={DESCRIPTION_FIELDS}
          original={creationDescription(values)}
          locked={locked}
          onLanguage={(language) => form.setFieldValue("descriptionLanguage", language)}
          onContent={(content) => form.setValues({ ...values, ...descriptionWritten(values, content) })}
        />
        <details className="creation-section">
          <summary>
            Presentation <span>Optional</span>
          </summary>
          <p className="text-muted">
            {presentationNote} You can upload event images after creation.
          </p>
          <div className="creation-previews">
            {previews.map(({ kind, src, caption }) => (
              <figure key={kind}>
                <img alt={"Default event " + kind} src={src} />
                <figcaption>{caption}</figcaption>
              </figure>
            ))}
          </div>
        </details>
      </fieldset>
      <EditorActions dirty={form.dirty} pending={locked} saveLabel="Create event" message={!organizersReady ? "Verify the club organizers before creating the event."
        : tenis10 ? "The Tenis10 competitions (level and age category) are added after creation." : "Competitions can be added after creation."}>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => history.push(eventDirectoryLocation())}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={locked || !organizersReady}
          >
            {status === "pending" ? "Creating…" : "Create event"}
          </button>
      </EditorActions>
    </form>
  )
}
export default connect(({ session: { settings, userDetails } = {} }) => ({
  settings,
  userDetails,
}))(CreateEvent)
